import { useState, useEffect } from 'react';
import { useAuthenticatedApi } from './useAuthenticatedApi';
import { apiEndpoints } from '../utils/api';

const useProjectFiles = (workspaceId) => {
  const authenticatedApi = useAuthenticatedApi();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchFiles = async () => {
    if (!workspaceId) return;
    
    setLoading(true);
    try {
      const filesData = await authenticatedApi.get(apiEndpoints.files(workspaceId));
      setFiles(filesData);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const endpoint = apiEndpoints.fileUpload(workspaceId);
      const uploadedFile = await authenticatedApi.uploadFile(endpoint, formData);
      setFiles(prev => [uploadedFile, ...(Array.isArray(prev) ? prev : [])]);
      return uploadedFile;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const uploadMultipleFiles = async (fileList) => {
    const formData = new FormData();

    const isSingleFile = fileList.length === 1;

    if (isSingleFile) {
      formData.append('file', fileList[0]);

      try {
        const endpoint = apiEndpoints.fileUpload(workspaceId);
        const uploadedFile = await authenticatedApi.uploadFile(endpoint, formData);
        setFiles(prev => [uploadedFile, ...(Array.isArray(prev) ? prev : [])]);
        return [uploadedFile];
      } catch (err) {
        setError(err.message || 'Failed to upload file');
        throw err;
      }
    } else {
      Array.from(fileList).forEach(file => {
        formData.append('files', file);
      });

      try {
        const endpoint = apiEndpoints.fileUploadMultiple(workspaceId);

        const uploadedFiles = await authenticatedApi.uploadFile(endpoint, formData);

        const filesArray = Array.isArray(uploadedFiles) ? uploadedFiles : [uploadedFiles];

        setFiles(prev => [...filesArray, ...(Array.isArray(prev) ? prev : [])]);
        return filesArray;
      } catch (err) {
        setError(err.message || 'Failed to upload files');
        throw err;
      }
    }
  };

  const deleteFile = async (fileId) => {
    try {
      await authenticatedApi.delete(apiEndpoints.file(workspaceId, fileId));
      setFiles(prev => prev.filter(f => f.id !== fileId));
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const downloadFile = async (fileId, filename) => {
    try {
      const { blob } = await authenticatedApi.downloadBlob(apiEndpoints.fileContent(workspaceId, fileId));
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError(err.message || 'Failed to download file');
      throw err;
    }
  };

  useEffect(() => {
    fetchFiles();
  }, [workspaceId]);

  return {
    files,
    loading,
    error,
    uploadFile,
    uploadMultipleFiles,
    deleteFile,
    downloadFile,
    refetch: fetchFiles,
    clearError: () => setError(null),
  };
};

export default useProjectFiles;
